import React from 'react';
import {
    Table,
    Typography,
    Button,
    Modal,
    Input,
    Space,
    Row,
    TableColumnsType
} from 'antd';
import {MailOutlined} from '@ant-design/icons';
import _ from 'lodash';

import {useAppSelector} from '../../../hooks/useStore';
import {useGetFormLettersQuery, useSendFormLetterMutation} from '../../../store/api/formLettersApi';
import {selectHasStaffRole} from '../../../store/slices/userSlice';

import PageTitle from '../../../components/page/PageTitle';
import {messageType, showMessages} from '../../../helpers/message';
import {BaseError} from '../../../types/apiTypes';

import {FormLetter} from '../../../types/formLettersTypes';
import {formItemFullWidth} from '../../../assets/styles';

import './FormLetters.less';

const {Title, Text} = Typography;

const FormLetters = () => {
    const compressRows = window.innerWidth <= 991;
    const isMobile = window.innerWidth <= 768;
    const hasAccess = useAppSelector(state => selectHasStaffRole(state, ['admin', 'developer', 'member-care', 'marketing']));

    const [page, setPage] = React.useState(1);
    const [perPage, setPerPage] = React.useState(10);
    const [totalRows, setTotalRows] = React.useState(10);

    const [selectedLetter, setSelectedLetter] = React.useState<FormLetter | null>(null);
    const [email, setEmail] = React.useState('');
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    const {data: lettersData, isFetching: isLoading, isSuccess, isError, error} = useGetFormLettersQuery({
        page,
        perPage
    });

    const [sendFormLetter, {
        isSuccess: sendSuccess,
        isError: isSendError,
        error: sendError
    }] = useSendFormLetterMutation();

    React.useEffect(() => {
        if (isSuccess && lettersData) {
            setTotalRows(lettersData.meta.total);
        } else if (isError && error) {
            showMessages((error as BaseError).data?.message, messageType.error);
        }
    }, [lettersData, error, isError, isSuccess]);

    React.useEffect(() => {
        if (sendSuccess) {
            showMessages('Form letter sent successfully.', messageType.success);
        } else if (isSendError && sendError) {
            showMessages((sendError as BaseError).data?.message, messageType.error);
        }
    }, [sendSuccess, sendError, isSendError]);

    const pageChange = (page: number, perPage: number) => {
        setPage(page);
        setPerPage(perPage);
    };

    const handleSendLetter = () => {
        if (!selectedLetter || !email) {
            showMessages('Please enter a staff email address.', messageType.error);
            return;
        }

        sendFormLetter({
            notification_id: selectedLetter.id,
            email
        })

        setIsModalOpen(false);
        setEmail('');
        setSelectedLetter(null);
    };

    const columns: TableColumnsType<FormLetter> = [
        {
            title: <Text strong>Name</Text>,
            dataIndex: 'name',
            key: 'name',
            render: (name: string) => <span className="long-text-wrap">
                {/*Convert from snake case to capital case.*/}
                {_.startCase(name)}
            </span>,
        },
        {
            title: <Text strong>Action</Text>,
            key: 'action',
            width: '120px',
            render: (_: any, record: FormLetter) => (
                <Button
                    className="form-letters-send-button"
                    type="link"
                    onClick={() => {
                        setSelectedLetter(record);
                        setIsModalOpen(true);
                    }}
                >
                    Send
                </Button>
            ),
        }
    ];

    const mobileExpandedRowRender = (record: FormLetter) => {
        return (
            <div className="form-letters-expanded-row-list">
                <Text strong className="form-letters-expanded-row-list-title">Action: </Text>
                <Button
                    type="primary" ghost size="small"
                    className="form-letters-send-button"
                    onClick={() => {
                        setSelectedLetter(record);
                        setIsModalOpen(true);
                    }}
                >
                    Send
                </Button>
            </div>
        );
    };

    const FormLettersHeader = (
        <Row className="form-letters-header-row">
            <Title className="form-letters-list-title" level={5}>Form Letters</Title>
            <span className="form-letters-subtitle">
                Send a preview of automatic emails to internal staff
            </span>
        </Row>
    );

    const paginationAttr = {
        current: page,
        pageSize: perPage,
        total: totalRows,
        showSizeChanger: true,
        simple: isMobile,
        onChange: pageChange,
        showTotal: (total: number, range: [number, number]) => `${range[0]}-${range[1]} of ${total} items`,
        onShowSizeChange: pageChange,
    };

    return (
        <>
            <div className="form-letters-page-title">
                <PageTitle title="Form Letters" icon={<MailOutlined/>}/>
            </div>

            {hasAccess ? (
                <Table
                    rowKey="id"
                    className="staff-form-letters-table"
                    loading={isLoading}
                    dataSource={lettersData?.data}
                    columns={compressRows ? [columns[0]] : columns}
                    expandable={compressRows ? {
                        expandedRowRender: mobileExpandedRowRender
                    } : undefined}
                    pagination={paginationAttr}
                    bordered={false}
                    caption={FormLettersHeader}
                />
            ) : (
                <Text className="role-disclaimer-text">
                    Sorry, you do not have the required permissions to view this page.
                </Text>
            )}

            <Modal
                title="Send Form Letter"
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                footer={[
                    <Button key="cancel" onClick={() => setIsModalOpen(false)}>
                        Cancel
                    </Button>,
                    <Button
                        key="send"
                        type="primary"
                        loading={isLoading}
                        onClick={handleSendLetter}
                    >
                        Send
                    </Button>,
                ]}
            >
                <Space direction="vertical" style={{...formItemFullWidth}}>
                    <Text>Which staff email would you like to send this to?</Text>
                    <Input
                        placeholder="Type staff email here"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                </Space>
            </Modal>
        </>
    );
};

export default FormLetters;
